import React, {useEffect, useRef, useState} from "react";

/**
 * Подпись у обратного отсчёта, который идёт по сроку внешней площадки.
 *
 * Названа площадка, а не «источник»: трейдер видит имя мерчанта вида
 * `paylord_*` на той же карточке, и «SOURCE» ему пришлось бы сопоставлять
 * самому. Кабинет сегодня один; когда появится второй, имя надо будет брать из
 * данных — префикс источника уже едет в `remoteId` (`paylord:697045`), а не
 * дописывать сюда второй литерал.
 */
export const SOURCE_DEADLINE_LABEL = "PAYLORD";

/**
 * Подпись у обратного отсчёта по СВОЕМУ сроку заявки.
 *
 * То же слово, что стоит в статусном чипе, и это не совпадение: пока идёт
 * отсчёт, «ждёт оплаты» и «сколько осталось» — один факт, и показываются они
 * одной плашкой, а не двумя жёлтыми подряд. Разные подписи в пуле и в работе
 * читались бы как два разных таймера — ровно так их и прочли.
 *
 * В пуле платежа нет вовсе, и PENDING здесь — слово интерфейса, а не
 * payments.status. Ветки показа завязаны на наличие СРОКА именно поэтому:
 * подключить сюда статус значит потерять подпись у свободной заявки.
 */
export const ORDER_DEADLINE_LABEL = "PENDING";

interface CountdownBadgeProps {
    /** ISO timestamp when the booking expires. */
    expiresAt: string;
    /** Fired exactly once when the countdown crosses zero (re-armed if expiresAt changes). */
    onExpire?: () => void;
    className?: string;
    /**
     * Подпись слева от значения, как у ElapsedBadge. Нужна там, где обратный
     * отсчёт стоит в одном ряду с возрастными таймерами и без подписи читался
     * бы как ещё один из них.
     */
    label?: string;
    /** Слова на месте значения после нуля и подсказка при наведении. */
    expiredText?: string;
    hint?: string;
    /**
     * Показывать `ч:мм:сс` вместо укороченного формата.
     *
     * Укороченный формат родился под бронь в двадцать минут, где ветка «час и
     * больше» практически не включалась. У срока площадки она включается почти
     * всегда, и тогда `02:57` рядом с `TOTAL 00:02:44` читается как «две минуты
     * пятьдесят семь секунд» — то есть ровно наоборот по срочности. Полный
     * формат совпадает с соседними бейджами, и глазу не надо угадывать разряды.
     */
    withSeconds?: boolean;
}

function pad(n: number): string {
    return n.toString().padStart(2, "0");
}

// mm:ss while under an hour, otherwise hh:mm.
function formatRemaining(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    if (totalSeconds < 3600) {
        return `${pad(Math.floor(totalSeconds / 60))}:${pad(totalSeconds % 60)}`;
    }
    return `${pad(Math.floor(totalSeconds / 3600))}:${pad(Math.floor((totalSeconds % 3600) / 60))}`;
}

// ч:мм:сс — те же разряды, что у ElapsedBadge рядом.
function formatRemainingLong(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    return `${pad(Math.floor(totalSeconds / 3600))}:${pad(Math.floor((totalSeconds % 3600) / 60))}:${pad(totalSeconds % 60)}`;
}

export function CountdownBadge({
    expiresAt,
    onExpire,
    className,
    label,
    expiredText = "бронь истекла",
    hint = "Время до истечения брони",
    withSeconds = false,
}: CountdownBadgeProps): JSX.Element {
    const [now, setNow] = useState(() => Date.now());
    const firedRef = useRef(false);
    const onExpireRef = useRef(onExpire);
    onExpireRef.current = onExpire;

    const target = new Date(expiresAt).getTime();
    const valid = Number.isFinite(target);
    const msLeft = target - now;
    const expired = valid && msLeft <= 0;

    // Re-arm the one-shot callback whenever the deadline changes.
    useEffect(() => {
        firedRef.current = false;
    }, [expiresAt]);

    // Tick once a second while the deadline is in the future; stop after it passes.
    useEffect(() => {
        if (!valid || expired) return;
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [valid, expired]);

    useEffect(() => {
        if (expired && !firedRef.current) {
            firedRef.current = true;
            onExpireRef.current?.();
        }
    }, [expired]);

    const base = `countdown-badge ${className ?? ""}`.trim();
    const caption = label ? <span className="countdown-label">{label}</span> : null;

    if (!valid) {
        return <span className={base}>{caption}—</span>;
    }
    if (expired) {
        return (
            <span className={`${base} expired`} title={expiredText}>
                {caption}{expiredText}
            </span>
        );
    }

    const urgent = msLeft < 60_000;
    return (
        <span className={`${base}${urgent ? " urgent" : ""}`} title={hint}>
            {caption}{withSeconds ? formatRemainingLong(msLeft) : formatRemaining(msLeft)}
        </span>
    );
}
